import { useThree } from "@react-three/fiber";
import { useDelayedEffect } from "@utils/useDelayedEffect";
import { useMemo, useRef } from "react";
import { CameraHelper, Color as ThreeColor, DirectionalLight, Object3D } from "three";
import { useViewportData } from "@components/ViewportManager";
import { AppData, Spring } from "@App";
import { animated, useSpring } from "react-spring";
import { EScenario } from "@utils/Scenarios";
import { closestNeutral, mapTime, maxTime, minTime, timeToColor, timeToValue } from "@utils/TimeMap";

const timeSpring: Spring = { tension: 350, friction: 30, mass: 2 };

export function Lighting({ debug = false }: { debug?: boolean }) {
    const { scenario, passedTime } = useViewportData<AppData>("main")!;

    const [{ value: time }] = useSpring({ to: { value: scenario === EScenario.Temperature ? passedTime : closestNeutral(passedTime) }, config: timeSpring }, [scenario, passedTime]);

    return (
        <ALighting time={time} debug={debug} />
    );
}

const ALighting = animated(({ time, debug = false }: { time: number, debug: boolean }) => {
    const dirLight = useRef<DirectionalLight>(null);
    const target = useRef<Object3D>(null);
    const { scene } = useThree();
    useDelayedEffect(() => {
        if (!dirLight.current) return;
        if (debug) scene.add(new CameraHelper(dirLight.current.shadow.camera));
    }, [dirLight, scene]);

    const angle = mapTime(time, Math.PI * 0.75, Math.PI * 0.75 + Math.PI * 2 * (maxTime - minTime) / 24);
    const x = Math.cos(angle) * 5;
    const z = Math.sin(angle) * 5;
    const y = timeToValue(time, 6, 2, 0);
    const intensity = timeToValue(time, 1.2, 0.5, 0.3);
    const [color, noon, golden, night] = useMemo(() => {
        return [
            new ThreeColor(),
            new ThreeColor("#ffffff"),
            new ThreeColor("#ffc938"),
            new ThreeColor("#3c525a")
        ]
    }, []);
    timeToColor(color, time, noon, golden, night);
    return (
        <>
            <hemisphereLight color={0xffffdd} groundColor={0x080820} intensity={intensity * 1.5} />
            <object3D ref={target} position={[0, 2, 0]} />
            {target.current && <directionalLight ref={dirLight} color={color} intensity={intensity} position={[x, y, z]} target={target.current} castShadow={true} shadow-camera-far={10} shadow-bias={-0.0015} shadow-normalBias={0.015} />}
        </>
    );
});